import React, { useEffect, useState } from 'react';
import axios from '../config/api';
import { useAuth } from '../context/AuthContext';
import DeleteAccountPanel from '../components/DeleteAccountPanel';

interface Settings {
  dark_mode: boolean;
  notifications_enabled: boolean;
}

export default function Settings() {
  const { updateUser } = useAuth();
  const [settings, setSettings] = useState<Settings>({
    dark_mode: false,
    notifications_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings');
      const fetchedSettings = response.data;
      setSettings(fetchedSettings);
      
      // Apply dark mode immediately if enabled
      if (fetchedSettings.dark_mode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401 || error.response?.data?.error?.includes('token')) {
        setMessage('Please log in to view settings');
      } else {
        setMessage(error.response?.data?.error || 'Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: keyof Settings, value: boolean) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setSaving(true);
    setMessage('');

    try {
      await axios.put('/api/settings', { [key]: value });
      setMessage('Settings saved successfully!');
      if (key === 'dark_mode') {
        // Apply dark mode immediately
        if (value) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error updating settings:', error);
      if (error.response?.status === 401 || error.response?.data?.error?.includes('token')) {
        setMessage('Please log in to change settings');
      } else {
        setMessage(error.response?.data?.error || 'Failed to save settings');
      }
      // Revert on error
      setSettings(settings);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading settings...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-3xl font-bold mb-6 text-amber-800">Settings</h1>

      {message && (
        <div className={`mb-4 p-3 rounded ${
          message.includes('success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Dark Mode */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">Dark Mode</h3>
            <p className="text-sm text-gray-600">Switch to dark theme for better viewing in low light</p>
          </div>
          <button
            type="button"
            onClick={() => updateSetting('dark_mode', !settings.dark_mode)}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
              settings.dark_mode ? 'bg-amber-600' : 'bg-gray-200'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={settings.dark_mode}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.dark_mode ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Notifications */}
        <div className="flex items-center justify-between border-t pt-6">
          <div>
            <h3 className="font-semibold text-lg mb-1">Notifications</h3>
            <p className="text-sm text-gray-600">Receive push notifications about orders and offers</p>
          </div>
          <button
            type="button"
            onClick={() => updateSetting('notifications_enabled', !settings.notifications_enabled)}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
              settings.notifications_enabled ? 'bg-amber-600' : 'bg-gray-200'
            } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            role="switch"
            aria-checked={settings.notifications_enabled}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.notifications_enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* App Info */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-2 text-red-800">Delete account</h3>
          <DeleteAccountPanel />
        </div>

        <div className="border-t pt-6">
          <h3 className="font-semibold text-lg mb-4">App Information</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p><strong>Version:</strong> 1.0.0</p>
            <p><strong>Platform:</strong> Mobile & Web</p>
          </div>
        </div>

      </div>
    </div>
  );
}
